import { useEffect, useCallback, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import styles from "./index.module.scss";
import PoiSearch from "../rescueAddress/PoiSearch";
import AmapMarker from "../rescueAddress/AmapMarker";
import MessageBox from "../../../components/message-box";
import AMap from "../../../components/AMapComp";
import SelectItem from "../components/SelectItem";
import SearchBarWithButton from "../components/SearchBarWithButton";
import useRescueAddressHook from "../common/hooks/useRescueAddressHook";
import useAMapHook from "../../../hooks/useAMapHook";
import useQuery from "../../../hooks/useQuery";
import { calculateDistance } from "../common/js/util";
import useTrailerAddress from "../common/hooks/useTrailerAddress";

export default function TrailerDestinationPage() {
  const history = useHistory();
  const query = useQuery();
  const initialInput = query.get("input");
  const [inputKeyword, setInputKeyword] = useState("");
  const [addressList, setAddressList] = useState<any>([]);
  const [selectAddress, setSelectAddress] = useState<any>({});
  const inputRef = useRef("");

  const { rescueAddress } = useRescueAddressHook();
  const { setTrailerAddress } = useTrailerAddress();

  const mapWrapper = useRef(null);
  const { mapEntity, loaded } = useAMapHook({
    container: mapWrapper.current,
  });

  // 仅执行一次
  useEffect(() => {
    document.title = "拖车目的地";
    // 从 URL 参数中获取初始搜索关键词
    if (initialInput) {
      setInputKeyword(initialInput);
      inputRef.current = initialInput;
    }
  }, []);

  const handleInputChange = useCallback((newInput: string) => {
    inputRef.current = newInput;
    setInputKeyword(newInput);

    if (newInput.length === 0) {
      setAddressList([]);
      setSelectAddress({});
    }
  }, []);

  // 拼接地址详细信息
  const getFullName = (selectAddress) => {
    return `${selectAddress.pname}${selectAddress.cityname}${selectAddress.adname}${selectAddress.name}`;
  };

  const handleSearched = useCallback(
    (list, keyword) => {
      if (keyword !== inputRef.current) {
        return;
      }

      // 按照距离远近排序
      const sortList = list
        .map((item) => {
          const distance = calculateDistance(rescueAddress, {
            longitude: item?.location?.lng,
            latitude: item?.location?.lat,
          });
          return {
            ...item,
            distance,
          };
        })
        .sort((a, b) => a.distance - b.distance);

      setAddressList(sortList);

      // 当第一个地址与初始值一样时，自动选中
      if (
        initialInput &&
        list.length &&
        initialInput === getFullName(list[0])
      ) {
        setSelectAddress(list[0]);
      } else {
        setSelectAddress({});
      }
    },
    [rescueAddress],
  );

  const handleSelectAddress = useCallback((address) => {
    setSelectAddress(address);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectAddress.name || !selectAddress?.location) {
      MessageBox.toast("请搜索并选择拖车目的地");
      return;
    }
    // 保存拖车目的地省份，城市，具体地址信息到拖车地址状态
    setTrailerAddress({
      province: selectAddress.pname,
      city: selectAddress.cityname,
      district: selectAddress.adname,
      address: `${selectAddress.pname}${selectAddress.cityname}${selectAddress.adname}${selectAddress.name}`,
      locationLat: selectAddress?.location?.lat,
      locationLon: selectAddress?.location?.lng,
    });

    history.goBack();
  }, [selectAddress, history, setTrailerAddress]);

  return (
    <div className={styles.pageTrailerDestination}>
      <div className={styles.mapContainer}>
        <AMap loaded={loaded} mapEntity={mapEntity} mapWrapper={mapWrapper}>
          {/* 直接搜索地址列表 */}
          <PoiSearch
            keyword={inputKeyword}
            adcode={rescueAddress?.cityAdcode || 0}
            onSearched={handleSearched}
          ></PoiSearch>
          {/* 地图上地点的自动标记（通过经纬度） */}
          <AmapMarker
            rescuePosition={{
              lng: selectAddress?.location?.lng,
              lat: selectAddress?.location?.lat,
            }}
          ></AmapMarker>
        </AMap>
      </div>
      <div className={styles.addressContainer}>
        <div className={styles.searchBarWrapper}>
          <SearchBarWithButton
            value={inputKeyword}
            onChange={handleInputChange}
            onConfirm={handleConfirm}
            placeholder="搜索地点"
          />
        </div>

        <div className={styles.results}>
          {addressList && addressList.length > 0 ? (
            addressList.map((item) => {
              return (
                <div key={item.id} className={styles.resultItem}>
                  <SelectItem
                    title={item.name}
                    distance={item.distance}
                    address={`${item.cityname} ${item.adname} ${item.address}`}
                    selected={selectAddress.id === item.id}
                    onClick={() => handleSelectAddress(item)}
                    highlightText={inputKeyword}
                  />
                </div>
              );
            })
          ) : (
            <div className={styles.searchTip}>
              <div className={styles.icon}></div>
              <div className={styles.tipTxt}>
                请输入具体地点信息，如：广州亚运城
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
